<template>
    <div v-if="hasLifecycle" class="request-lifecycle">
        <button
            type="button"
            class="request-lifecycle__summary"
            :aria-expanded="open ? 'true' : 'false'"
            @click="open = !open"
        >
            <span class="request-lifecycle__summary-title">
                <i class="fas fa-stream" aria-hidden="true"></i>
                <span>Жизненный цикл</span>
            </span>
            <span class="request-lifecycle__summary-text">{{ summaryText }}</span>
            <i
                class="fas request-lifecycle__summary-icon"
                :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"
                aria-hidden="true"
            ></i>
        </button>

        <b-collapse v-model="open">
            <div class="request-lifecycle__body">
                <div v-if="lifecycle.incomplete" class="request-lifecycle__notice">
                    <i class="fas fa-info-circle" aria-hidden="true"></i>
                    <span>История этапов неполная, расчет построен по доступным событиям.</span>
                </div>

                <div class="request-lifecycle__timeline">
                    <div
                        v-for="(stage, index) in stages"
                        :key="`${stage.status}-${stage.started_at || index}-${index}`"
                        class="request-lifecycle__stage"
                        :class="{
                            'request-lifecycle__stage--current': stage.is_current,
                            'request-lifecycle__stage--terminal': stage.is_terminal,
                            'request-lifecycle__stage--milestone': stage.kind === 'milestone'
                        }"
                    >
                        <span class="request-lifecycle__stage-line" aria-hidden="true"></span>
                        <span
                            class="request-lifecycle__stage-dot"
                            :class="`request-lifecycle__stage-dot--${stage.variant || 'secondary'}`"
                            aria-hidden="true"
                        ></span>
                        <div class="request-lifecycle__stage-content">
                            <div class="request-lifecycle__stage-head">
                                <b-badge :variant="stage.variant || 'secondary'">
                                    {{ stage.display_label || stage.label || stage.status }}
                                </b-badge>
                                <strong
                                    v-if="stage.kind !== 'milestone'"
                                    class="request-lifecycle__duration"
                                >
                                    {{ stage.duration_text || '0 мин' }}
                                </strong>
                                <time
                                    v-else
                                    class="request-lifecycle__milestone-time"
                                    :datetime="stage.occurred_at || stage.started_at"
                                >
                                    {{ formatDate(stage.occurred_at || stage.started_at) }}
                                </time>
                            </div>
                            <div
                                v-if="stage.kind === 'approval' && stage.approval"
                                class="request-lifecycle__approval"
                            >
                                <div class="request-lifecycle__approval-summary">
                                    <b-badge v-if="stage.approval.automatic" variant="info">
                                        Автоматически
                                    </b-badge>
                                    <strong v-if="stage.is_current && stage.approval.progress_text">
                                        {{ stage.approval.progress_text }}
                                    </strong>
                                    <span v-else-if="stage.approval.outcome_label">
                                        Итог: <strong>{{ stage.approval.outcome_label }}</strong>
                                    </span>
                                    <span class="request-lifecycle__approval-mode">
                                        {{ stage.approval.sequential ? 'Последовательно' : 'Параллельно' }}
                                    </span>
                                </div>

                                <div
                                    v-if="stage.approval.approvers && stage.approval.approvers.length"
                                    class="request-lifecycle__approvers"
                                >
                                    <div
                                        v-for="(approver, approverIndex) in stage.approval.approvers"
                                        :key="`${stage.occurrence}-${approver.participant_id || approver.user_id || approverIndex}-${approverIndex}`"
                                        class="request-lifecycle__approver"
                                    >
                                        <span
                                            v-if="stage.approval.sequential"
                                            class="request-lifecycle__approver-order"
                                            aria-hidden="true"
                                        >
                                            {{ approverIndex + 1 }}
                                        </span>
                                        <TelegramUserLink
                                            v-if="approver.user"
                                            class="request-lifecycle__approver-name"
                                            :user="approver.user"
                                            name-order="last-first"
                                            :fallback="approver.name || 'Сотрудник не указан'"
                                        />
                                        <span v-else class="request-lifecycle__approver-name">
                                            {{ approver.name || 'Сотрудник не указан' }}
                                        </span>
                                        <b-badge :variant="approver.variant || 'secondary'">
                                            {{ approver.status_label || approver.status }}
                                        </b-badge>
                                        <span
                                            v-if="approver.duration_label"
                                            class="request-lifecycle__approver-duration"
                                            :title="approver.duration_started_at ? `Календарное время с ${formatDate(approver.duration_started_at)}` : null"
                                        >
                                            <i class="far fa-clock" aria-hidden="true"></i>
                                            {{ approver.duration_label }}
                                        </span>
                                        <span
                                            v-if="approver.is_late_approver"
                                            class="request-lifecycle__approver-late"
                                        >
                                            дополнительный
                                        </span>
                                        <span
                                            v-if="approver.decided_at"
                                            class="request-lifecycle__approver-time"
                                        >
                                            {{ formatDate(approver.decided_at) }}
                                        </span>
                                        <div
                                            v-if="approver.comment"
                                            class="request-lifecycle__approver-comment"
                                        >
                                            <i class="fas fa-comment-alt" aria-hidden="true"></i>
                                            <div>
                                                <strong>{{ approver.comment_label }}</strong>
                                                <span
                                                    v-if="approver.admin_override"
                                                    class="request-lifecycle__approver-comment-admin"
                                                >
                                                    Действие выполнил администратор.
                                                </span>
                                                <span class="request-lifecycle__approver-comment-text">
                                                    {{ approver.comment }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="stage.approval.events && stage.approval.events.length"
                                    class="request-lifecycle__approval-events"
                                >
                                    <div
                                        v-for="(event, eventIndex) in stage.approval.events"
                                        :key="`${stage.occurrence}-${event.action}-${event.at || eventIndex}-${eventIndex}`"
                                        class="request-lifecycle__approval-event"
                                    >
                                        <i class="fas fa-exchange-alt" aria-hidden="true"></i>
                                        <span>{{ event.description }}</span>
                                        <span v-if="event.at" class="request-lifecycle__approval-event-time">
                                            {{ formatDate(event.at) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="stage.kind !== 'milestone'"
                                class="request-lifecycle__stage-time"
                            >
                                <span>{{ formatDate(stage.started_at) }}</span>
                                <span class="request-lifecycle__stage-arrow">→</span>
                                <span>{{ stageEndText(stage) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import TelegramUserLink from './TelegramUserLink.vue';

export default {
    name: 'RequestLifecycle',
    components: {
        TelegramUserLink
    },
    props: {
        request: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            open: false
        };
    },
    computed: {
        lifecycle() {
            return this.request && this.request.stage_lifecycle ? this.request.stage_lifecycle : null;
        },
        stages() {
            return this.lifecycle && Array.isArray(this.lifecycle.stages)
                ? this.lifecycle.stages
                : [];
        },
        hasLifecycle() {
            return this.stages.length > 0;
        },
        currentStage() {
            return this.lifecycle && this.lifecycle.current_stage ? this.lifecycle.current_stage : null;
        },
        summaryText() {
            if (!this.lifecycle || !this.currentStage) {
                return '';
            }

            const label = this.currentStage.display_label ||
                this.currentStage.label ||
                this.currentStage.status ||
                'этап';
            const total = this.lifecycle.total_duration_text
                ? ` · всего ${this.lifecycle.total_duration_text}`
                : '';
            const approvalProgress = this.currentStage.kind === 'approval' &&
                this.currentStage.approval &&
                this.currentStage.approval.progress_text
                ? ` · ${this.currentStage.approval.progress_text.toLowerCase()}`
                : '';

            if (this.currentStage.is_current) {
                return `сейчас «${label}» ${this.currentStage.duration_text || '0 мин'}${approvalProgress}${total}`;
            }

            return `завершено: ${this.lifecycle.total_duration_text || '0 мин'}`;
        }
    },
    methods: {
        formatDate(value) {
            if (!value) {
                return 'неизвестно';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return 'неизвестно';
            }

            const datePart = date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
            const timePart = date.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit'
            });

            return `${datePart} ${timePart}`;
        },
        stageEndText(stage) {
            if (!stage) {
                return 'неизвестно';
            }
            if (stage.is_current) {
                return 'сейчас';
            }
            if (stage.is_terminal) {
                return 'финальный статус';
            }
            return this.formatDate(stage.ended_at);
        }
    }
};
</script>

<style scoped>
.request-lifecycle {
    margin-top: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: #fff;
}

.request-lifecycle__summary {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    padding: 0.65rem 0.85rem;
    border: 0;
    background: #f8f9fa;
    color: #212529;
    text-align: left;
}

.request-lifecycle__summary:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.request-lifecycle__summary-title {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-weight: 700;
    white-space: nowrap;
}

.request-lifecycle__summary-title i {
    color: #17a2b8;
}

.request-lifecycle__summary-text {
    min-width: 0;
    color: #495057;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-lifecycle__summary-icon {
    color: #6c757d;
}

.request-lifecycle__body {
    padding: 0.8rem 0.85rem 0.9rem;
}

.request-lifecycle__notice {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-bottom: 0.75rem;
    padding: 0.45rem 0.6rem;
    border-left: 3px solid #17a2b8;
    background: #eef9fb;
    color: #495057;
    font-size: 0.85rem;
    line-height: 1.35;
}

.request-lifecycle__timeline {
    display: flex;
    flex-direction: column;
}

.request-lifecycle__stage {
    position: relative;
    display: grid;
    grid-template-columns: 1.1rem minmax(0, 1fr);
    column-gap: 0.55rem;
    min-height: 3.15rem;
}

.request-lifecycle__stage-line {
    position: absolute;
    top: 1rem;
    bottom: 0;
    left: 0.5rem;
    width: 1px;
    background: #dee2e6;
}

.request-lifecycle__stage:last-child .request-lifecycle__stage-line {
    display: none;
}

.request-lifecycle__stage-dot {
    position: relative;
    z-index: 1;
    grid-column: 1;
    width: 0.72rem;
    height: 0.72rem;
    margin-top: 0.35rem;
    margin-left: 0.15rem;
    border-radius: 50%;
    background: #6c757d;
    box-shadow: 0 0 0 3px #fff;
}

.request-lifecycle__stage-dot--primary { background: #007bff; }
.request-lifecycle__stage-dot--secondary { background: #6c757d; }
.request-lifecycle__stage-dot--success { background: #28a745; }
.request-lifecycle__stage-dot--danger { background: #dc3545; }
.request-lifecycle__stage-dot--warning { background: #ffc107; }
.request-lifecycle__stage-dot--info { background: #17a2b8; }
.request-lifecycle__stage-dot--dark { background: #343a40; }

.request-lifecycle__stage-content {
    grid-column: 2;
    min-width: 0;
    padding-bottom: 0.85rem;
}

.request-lifecycle__stage-head {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    min-width: 0;
    flex-wrap: wrap;
}

.request-lifecycle__approval {
    margin-top: 0.45rem;
    padding: 0.55rem 0.65rem;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    background: #fafbfc;
}

.request-lifecycle__approval-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
    color: #495057;
    font-size: 0.82rem;
}

.request-lifecycle__approval-mode {
    color: #6c757d;
}

.request-lifecycle__approvers {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 0.55rem;
}

.request-lifecycle__approver {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    min-width: 0;
    font-size: 0.82rem;
}

.request-lifecycle__approver-order {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: #e9ecef;
    color: #495057;
    font-size: 0.72rem;
    font-weight: 700;
}

.request-lifecycle__approver-name {
    min-width: 0;
    font-weight: 600;
}

.request-lifecycle__approver-late,
.request-lifecycle__approver-time {
    color: #6c757d;
    font-size: 0.76rem;
}

.request-lifecycle__approver-duration {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: #495057;
    font-size: 0.8rem;
    font-weight: 600;
}

.request-lifecycle__approver-duration i { color: #6c757d; }

.request-lifecycle__approver-late {
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
    background: #fff3cd;
    color: #856404;
}

.request-lifecycle__stage--milestone {
    min-height: 2.45rem;
}

.request-lifecycle__stage--milestone .request-lifecycle__stage-content {
    padding-bottom: 0.6rem;
}

.request-lifecycle__stage--milestone .request-lifecycle__stage-head {
    justify-content: flex-start;
    flex-wrap: nowrap;
}

.request-lifecycle__approver-comment {
    display: flex;
    align-items: flex-start;
    flex-basis: 100%;
    gap: 0.4rem;
    margin-top: 0.15rem;
    padding: 0.45rem 0.55rem;
    border-left: 3px solid #28a745;
    border-radius: 3px;
    background: #f1f8f3;
    color: #343a40;
    line-height: 1.4;
}

.request-lifecycle__approver-comment > i {
    flex: 0 0 auto;
    margin-top: 0.2rem;
    color: #28a745;
}

.request-lifecycle__approver-comment-admin,
.request-lifecycle__approver-comment-text {
    display: block;
    margin-top: 0.2rem;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.request-lifecycle__approver-comment-admin {
    color: #6c757d;
    font-size: 0.76rem;
}

.request-lifecycle__approval-events {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.55rem;
    padding-top: 0.5rem;
    border-top: 1px dashed #dee2e6;
}

.request-lifecycle__approval-event {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.35rem;
    color: #495057;
    font-size: 0.78rem;
    line-height: 1.35;
}

.request-lifecycle__approval-event i {
    margin-top: 0.15rem;
    color: #6c757d;
}

.request-lifecycle__approval-event-time {
    color: #6c757d;
    white-space: nowrap;
}

.request-lifecycle__duration {
    flex: 0 0 auto;
    color: #212529;
    font-size: 0.9rem;
    white-space: nowrap;
}

.request-lifecycle__milestone-time {
    flex: 0 0 auto;
    color: #6c757d;
    font-size: 0.82rem;
    font-weight: 600;
    white-space: nowrap;
}

.request-lifecycle__stage-time {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.25rem;
    color: #6c757d;
    font-size: 0.82rem;
    line-height: 1.35;
}

.request-lifecycle__stage-arrow {
    color: #adb5bd;
}

@media (max-width: 575.98px) {
    .request-lifecycle__summary {
        grid-template-columns: minmax(0, 1fr) auto;
        row-gap: 0.25rem;
    }

    .request-lifecycle__summary-text {
        grid-column: 1 / 3;
        white-space: normal;
    }

    .request-lifecycle__summary-icon {
        grid-column: 2;
        grid-row: 1;
    }

    .request-lifecycle__stage-head {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.25rem;
    }

    .request-lifecycle__stage--milestone .request-lifecycle__stage-head {
        align-items: center;
        flex-direction: row;
    }

    .request-lifecycle__approval {
        padding: 0.5rem;
    }

    .request-lifecycle__approver-time {
        flex-basis: 100%;
        padding-left: 1.65rem;
    }
}
</style>
